import React, { useMemo, useState } from 'react';

import { startOfDay, endOfDay } from 'date-fns';

import { DateRangePicker } from '@adsk/alloy-react-date-picker';
import { VirtualizedMenu, MenuItem } from '@adsk/alloy-react-dropdown';
import HighlightText from '@adsk/alloy-react-highlight-text';
import { useLocale } from '@adsk/alloy-react-locale';

import {
  StyledPackagesFilter,
  StyledPackagesFilterHeader,
  StyledPackagesFilterResetButton,
  StyledPackagesFilterBody,
  StyledFormField,
  StyledDropdown,
} from './PackagesFilter.styles';
import { usePackagesSearchFilter } from './PackagesSearchFilterProvider';
import { useProjectUsers } from './ProjectEntitiesProvider';
import { MAX_FILTER_ITEMS } from '../helpers/consts';
import getFilterCount from '../helpers/getFilterCount';

const PackagesFilter = () => {
  const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
  const [createdAtInputValue, setCreatedAtInputValue] = useState();
  const [updatedAtInputValue, setUpdatedAtInputValue] = useState();
  const { panelFilters, setPanelFilters } = usePackagesSearchFilter();
  const { data: projectUsers, loading: isProjectUsersLoading } =
    useProjectUsers();

  const filterCount = getFilterCount(panelFilters);

  const handleResetFilter = () => {
    setPanelFilters(() => ({}));
  };

  const handleCreatedAtChange = ({ startDate, endDate }) => {
    setPanelFilters({
      createdAt: [
        startDate && startOfDay(startDate),
        endDate && endOfDay(endDate),
      ],
    });
  };

  const handleCreatedByChange = ({ selectedItems: createdBy }) => {
    setPanelFilters({
      createdBy: createdBy ? createdBy.map((user) => user.value) : [],
    });
  };

  const handleUpdatedAtChange = ({ startDate, endDate }) => {
    setPanelFilters({
      updatedAt: [
        startDate && startOfDay(startDate),
        endDate && endOfDay(endDate),
      ],
    });
  };

  const handleUpdatedByChange = ({ selectedItems: updatedBy }) => {
    setPanelFilters({
      updatedBy: updatedBy ? updatedBy.map((user) => user.value) : [],
    });
  };

  const handleCreatedAtInputValueChange = (changes) => {
    setCreatedAtInputValue(changes.inputValue);
  };

  const handleUpdatedAtInputValueChange = (changes) => {
    setUpdatedAtInputValue(changes.inputValue);
  };

  const getFilterErrorMessage = (value) => {
    if (!value || value.length <= MAX_FILTER_ITEMS) {
      return null;
    }

    return T.translate('FILTER.MAX_EXCEED', { maxLimit: MAX_FILTER_ITEMS });
  };

  const projectUserItems = projectUsers.map((projectUser) => ({
    label: projectUser.name,
    value: projectUser.oxygenId,
    subtext: projectUser?.company?.name ?? '',
  }));

  const createdByErrorMessage = getFilterErrorMessage(panelFilters.createdBy);
  const updatedByErrorMessage = getFilterErrorMessage(panelFilters.updatedBy);
  const createdByValue = useMemo(() => {
    return panelFilters.createdBy
      ? projectUserItems.filter((user) =>
          panelFilters.createdBy.includes(user.value),
        )
      : [];
  }, [projectUserItems, panelFilters.createdBy]);
  const updatedByValue = useMemo(() => {
    return panelFilters.updatedBy
      ? projectUserItems.filter((user) =>
          panelFilters.updatedBy.includes(user.value),
        )
      : [];
  }, [projectUserItems, panelFilters.updatedBy]);

  /* eslint-disable react/prop-types */
  const renderMenuItem = (props) => (
    <MenuItem
      {...props}
      text={<HighlightText matched={props.matched} text={props.text} />}
      subtext={<HighlightText matched={props.matched} text={props.subtext} />}
    />
  );
  /* eslint-enable react/prop-types */

  return (
    <StyledPackagesFilter>
      <StyledPackagesFilterHeader>
        {T.translate('FILTER.TITLE', { context: filterCount })}
        <StyledPackagesFilterResetButton
          $hidden={!filterCount}
          onClick={handleResetFilter}
        >
          {T.translate('BUTTON.CLEAR')}
        </StyledPackagesFilterResetButton>
      </StyledPackagesFilterHeader>
      <StyledPackagesFilterBody>
        <StyledFormField label={T.translate('FILTER.CREATED_AT.TITLE')}>
          <DateRangePicker
            allowSameStartAndEndDate
            startDate={panelFilters.createdAt?.[0] ?? null}
            endDate={panelFilters.createdAt?.[1] ?? null}
            onChange={handleCreatedAtChange}
            triggerProps={{ style: { width: '100%' } }}
          />
        </StyledFormField>
        <StyledFormField
          label={T.translate('FILTER.CREATED_BY.TITLE')}
          error={createdByErrorMessage}
        >
          <StyledDropdown
            error={!!createdByErrorMessage}
            isLoading={isProjectUsersLoading}
            placeholder={T.translate('FILTER.CREATED_BY.PLACEHOLDER')}
            selectedItems={createdByValue}
            items={projectUserItems}
            renderMenu={(menuProps, virtualProps) => {
              return VirtualizedMenu.render(
                {
                  ...menuProps,
                  renderMenuItem: (props) => {
                    return renderMenuItem({
                      ...props,
                      matched: createdAtInputValue,
                    });
                  },
                  renderFooter: () => null,
                },
                virtualProps,
              );
            }}
            onInputValueChange={handleCreatedAtInputValueChange}
            onSelectedItemsChange={handleCreatedByChange}
          />
        </StyledFormField>
        <StyledFormField label={T.translate('FILTER.UPDATED_AT.TITLE')}>
          <DateRangePicker
            allowSameStartAndEndDate
            startDate={panelFilters.updatedAt?.[0] ?? null}
            endDate={panelFilters.updatedAt?.[1] ?? null}
            onChange={handleUpdatedAtChange}
            triggerProps={{ style: { width: '100%' } }}
          />
        </StyledFormField>
        <StyledFormField
          label={T.translate('FILTER.UPDATED_BY.TITLE')}
          error={updatedByErrorMessage}
        >
          <StyledDropdown
            error={!!updatedByErrorMessage}
            isLoading={isProjectUsersLoading}
            placeholder={T.translate('FILTER.UPDATED_BY.PLACEHOLDER')}
            selectedItems={updatedByValue}
            items={projectUserItems}
            renderMenu={(menuProps, virtualProps) => {
              return VirtualizedMenu.render(
                {
                  ...menuProps,
                  renderMenuItem: (props) => {
                    return renderMenuItem({
                      ...props,
                      matched: updatedAtInputValue,
                    });
                  },
                  renderFooter: () => null,
                },
                virtualProps,
              );
            }}
            onInputValueChange={handleUpdatedAtInputValueChange}
            onSelectedItemsChange={handleUpdatedByChange}
          />
        </StyledFormField>
      </StyledPackagesFilterBody>
    </StyledPackagesFilter>
  );
};

PackagesFilter.propTypes = {};

export default PackagesFilter;
